import React, { useState, useEffect, useCallback } from 'react';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Hotel Config → WhatsApp (per property).
 *
 * Four tabs:
 *   Channel    — this hotel's WhatsApp Business number, optional token
 *                override, quiet hours, invoice link lifetime
 *   Templates  — the hotel's own templates, mirroring what it approved on the
 *                CERF portal; the ordered {{variables}} become CERF's params
 *   Events     — which events send, with per-event delay and quiet-hours
 *   Logs       — send history + pending deferred sends
 *
 * The platform vendor account lives in Platform Console → WhatsApp.
 *
 * IMPORTANT: the body typed here is NOT what WhatsApp sends. Meta renders its
 * own approved copy; we supply positional values only. That is why a template
 * must pass a real test send before its event can be enabled.
 * See docs/SRS_WHATSAPP_CERF_INTEGRATION.md §6 / §12.2.
 */

const API = '/api/v1/whatsapp';

const TABS = [
  { key: 'channel', label: 'Channel' },
  { key: 'templates', label: 'Message Templates' },
  { key: 'events', label: 'Events' },
  { key: 'logs', label: 'Logs' },
];

// Check-out exclusion: the combined event carries the invoice link AND the
// review link, so enabling it must take the two single events off the table.
// Mirrored on the backend so a directly-edited DB row cannot double-send.
const COMBINED_CHECKOUT_EVENT = 'CHECKOUT_INVOICE_REVIEW';
const SINGLE_CHECKOUT_EVENTS = ['CHECKOUT_INVOICE', 'CHECKOUT_REVIEW'];

const DELAY_PRESETS = [
  { mins: 0, label: 'Immediately' },
  { mins: 60, label: '1 hour later' },
  { mins: 240, label: '4 hours later' },
  { mins: 720, label: '12 hours later' },
  { mins: 1440, label: 'Next day' },
];

export default function WhatsAppHotelSettings({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [tab, setTab] = useState('channel');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [canConfigure, setCanConfigure] = useState(false);

  const [config, setConfig] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [events, setEvents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [reviewUrl, setReviewUrl] = useState('');

  // Template editor
  const [editor, setEditor] = useState(null);   // null = closed
  const [savingTpl, setSavingTpl] = useState(false);
  const [testFor, setTestFor] = useState(null); // template id
  const [testMobile, setTestMobile] = useState('');
  const [testRunning, setTestRunning] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [tokenEditing, setTokenEditing] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const clearBanners = () => { setError(''); setMessage(''); };

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API}${path}`, { headers: headers(), ...options });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || `Request failed (${res.status})`);
    return data;
  }, [headers]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      const [cfg, tpl, evt] = await Promise.all([
        api('/config'), api('/templates'), api('/events'),
      ]);
      setConfig(cfg.config);
      setPlatform(cfg.platform);
      setCanConfigure(Boolean(cfg.can_configure));
      setTemplates(Array.isArray(tpl) ? tpl : []);
      setEvents(Array.isArray(evt) ? evt : []);

      // The review link comes from the Google Place ID confirmed on the Google
      // Review screen. Without it a review message silently skips on
      // MISSING_VARIABLES, so we check up front and say so.
      try {
        const gr = await fetch('/api/v1/google-review/config', { headers: headers() });
        const grData = await gr.json();
        setReviewUrl(gr.ok ? (grData.review_url || '') : '');
      } catch { setReviewUrl(''); }
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [selectedProperty, api, headers]);

  useEffect(() => { load(); }, [load]);

  const loadLogs = useCallback(async () => {
    try {
      const [l, q] = await Promise.all([api('/logs?limit=100'), api('/queue')]);
      setLogs(Array.isArray(l) ? l : []);
      setQueue(Array.isArray(q) ? q : []);
    } catch (err) { setError(err.message); }
  }, [api]);

  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);

  // ── Channel ──────────────────────────────────────────────────────────────
  const saveConfig = async (patch) => {
    clearBanners();
    try {
      const data = await api('/config', { method: 'PUT', body: JSON.stringify(patch) });
      if (data.config) setConfig(data.config);
      setMessage('Saved.');
      setTokenEditing(false);
    } catch (err) { setError(err.message); }
  };

  // ── Templates ────────────────────────────────────────────────────────────
  const openEditor = (tpl) => {
    clearBanners();
    setTestResult(null);
    setEditor(tpl ? { ...tpl } : {
      id: null, template_code: '', display_name: '', provider_template_id: '',
      event_code: '', body: '', is_active: true,
    });
  };

  const eventMeta = (code) => events.find(e => e.event_code === code);
  const paletteFor = (code) => {
    const e = eventMeta(code);
    if (!e) return [];
    return [...(e.required_variables || []), ...(e.optional_variables || [])];
  };

  // Live preview of the positional order — this is what actually goes on the wire.
  const orderedVars = (body) => {
    const found = [...String(body || '').matchAll(/\{\{\s*(\w+)\s*\}\}/g)].map(m => m[1]);
    return found.filter((v, i) => found.indexOf(v) === i);
  };

  const saveTemplate = async () => {
    if (!editor) return;
    clearBanners();
    setSavingTpl(true);
    try {
      const payload = {
        template_code: editor.template_code,
        display_name: editor.display_name,
        provider_template_id: editor.provider_template_id,
        event_code: editor.event_code,
        body: editor.body,
        is_active: editor.is_active,
      };
      const saved = editor.id
        ? await api(`/templates/${editor.id}`, { method: 'PUT', body: JSON.stringify(payload) })
        : await api('/templates', { method: 'POST', body: JSON.stringify(payload) });
      setMessage(saved.retest_required
        ? 'Saved. The variable order changed, so this template must pass a new test send before its event can be enabled.'
        : 'Template saved.');
      setEditor(null);
      await load();
    } catch (err) { setError(err.message); }
    finally { setSavingTpl(false); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.template_code}"?`)) return;
    clearBanners();
    try {
      await api(`/templates/${tpl.id}`, { method: 'DELETE' });
      setMessage('Template deleted.');
      await load();
    } catch (err) { setError(err.message); }
  };

  const runTest = async (tpl) => {
    setTestRunning(true); setTestResult(null);
    try {
      const data = await api(`/templates/${tpl.id}/test`, {
        method: 'POST', body: JSON.stringify({ mobile: testMobile }),
      });
      setTestResult(data);
      if (data.success) await load();
    } catch (err) {
      setTestResult({ success: false, error_message: err.message });
    } finally { setTestRunning(false); }
  };

  // ── Events ───────────────────────────────────────────────────────────────
  const saveEvent = async (code, patch) => {
    clearBanners();
    try {
      await api(`/events/${code}`, { method: 'PUT', body: JSON.stringify(patch) });
      await load();
    } catch (err) { setError(err.message); }
  };

  const cancelQueued = async (id) => {
    try { await api(`/queue/${id}/cancel`, { method: 'POST' }); await loadLogs(); }
    catch (err) { setError(err.message); }
  };

  // ── Styles ───────────────────────────────────────────────────────────────
  const card = { background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', padding: '1rem 1.25rem', marginBottom: '1rem' };
  const label = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '0.5rem 0.7rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.88rem', boxSizing: 'border-box' };
  const btn = { padding: '0.45rem 0.9rem', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 };
  const hint = { fontSize: '0.72rem', color: '#64748b', marginTop: 3 };
  const chip = (bg, fg) => ({ display: 'inline-block', padding: '0.12rem 0.5rem', borderRadius: 999, fontSize: '0.7rem', fontWeight: 700, background: bg, color: fg });

  if (!selectedProperty?.id) {
    return <div style={{ padding: '1.25rem', color: '#6b7280' }}>Select a property first.</div>;
  }

  const platformBlocked = platform && (!platform.configured || !platform.is_active || !platform.enabled_all_properties);
  const combinedCheckoutOn = events.some(e => e.event_code === COMBINED_CHECKOUT_EVENT && e.is_enabled);

  return (
    <div style={{ padding: '1.25rem', maxWidth: 1100 }}>
      <h2 style={{ margin: '0 0 0.25rem', color: '#1e3a8a' }}>WhatsApp</h2>
      <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '1rem' }}>
        Send messages to guests over WhatsApp — starting with the invoice at check-out.
        Templates must first be approved on the CERF portal; here you mirror them and map the variables.
      </div>

      {error && <div style={{ ...card, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }}>{error}</div>}
      {message && <div style={{ ...card, background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0' }}>{message}</div>}

      {platformBlocked && (
        <div style={{ ...card, background: '#fffbeb', border: '1px solid #fde68a', color: '#78350f', fontSize: '0.85rem' }}>
          <b>WhatsApp is not active at the platform level.</b>{' '}
          {!platform.configured
            ? 'No vendor account has been configured yet.'
            : !platform.is_active
              ? 'The provider is marked inactive.'
              : 'The "enable for all properties" master switch is off.'}
          {' '}Nothing will send until an EaseRoom administrator turns it on. You can still prepare templates here.
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => { setTab(t.key); clearBanners(); }}
                  style={{
                    ...btn,
                    background: tab === t.key ? '#2563eb' : '#fff',
                    color: tab === t.key ? '#fff' : '#1e40af',
                    border: tab === t.key ? 'none' : '1px solid #dbeafe',
                  }}>{t.label}</button>
        ))}
      </div>

      {loading && <div style={{ color: '#6b7280', fontSize: '0.85rem', marginBottom: '0.6rem' }}>Loading…</div>}

      {/* ─── Channel ─────────────────────────────────────────────────────── */}
      {tab === 'channel' && config && (
        <div style={card}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={Boolean(config.is_enabled)} disabled={!canConfigure}
                   onChange={e => saveConfig({ is_enabled: e.target.checked })} />
            <span style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1f2937' }}>Enable WhatsApp for this property</span>
          </label>
          <div style={{ ...hint, marginBottom: '1rem' }}>
            Both this and the platform master switch must be on for any message to send.
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: '0.9rem' }}>
            <div>
              <label style={label}>WhatsApp Business number</label>
              <input defaultValue={config.sender_phone || ''} disabled={!canConfigure}
                     onBlur={e => saveConfig({ sender_phone: e.target.value })}
                     placeholder="919943018468" style={inputStyle} />
              <div style={hint}>Your own registered sender number (no +). Leave blank to use the platform number.</div>
            </div>

            <div>
              <label style={label}>CERF API token override (optional)</label>
              {config.api_token_override_set && !tokenEditing ? (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input value="••••••••••" readOnly style={{ ...inputStyle, background: '#f1f5f9', color: '#64748b' }} />
                  <button onClick={() => setTokenEditing(true)} disabled={!canConfigure}
                          style={{ ...btn, background: '#fff', color: '#1e40af', border: '1px solid #dbeafe', whiteSpace: 'nowrap' }}>Change</button>
                  <button onClick={() => saveConfig({ remove_token: true })} disabled={!canConfigure}
                          style={{ ...btn, background: '#fff', color: '#991b1b', border: '1px solid #fecaca', whiteSpace: 'nowrap' }}>Remove</button>
                </div>
              ) : (
                <input type="password" autoComplete="new-password" disabled={!canConfigure}
                       onBlur={e => e.target.value && saveConfig({ api_token_override: e.target.value })}
                       placeholder="Leave blank to use the platform token" style={inputStyle} />
              )}
              <div style={hint}>Only needed if this hotel has its own CERF account.</div>
            </div>

            <div>
              <label style={label}>Quiet hours start</label>
              <input type="time" defaultValue={(config.quiet_hours_start || '').slice(0, 5)} disabled={!canConfigure}
                     onBlur={e => saveConfig({ quiet_hours_start: e.target.value || null })} style={inputStyle} />
            </div>
            <div>
              <label style={label}>Quiet hours end</label>
              <input type="time" defaultValue={(config.quiet_hours_end || '').slice(0, 5)} disabled={!canConfigure}
                     onBlur={e => saveConfig({ quiet_hours_end: e.target.value || null })} style={inputStyle} />
            </div>

            <div>
              <label style={label}>Timezone</label>
              <input defaultValue={config.timezone || 'Asia/Kolkata'} disabled={!canConfigure}
                     onBlur={e => saveConfig({ timezone: e.target.value })} style={inputStyle} />
              <div style={hint}>Quiet hours are evaluated in this timezone.</div>
            </div>

            <div>
              <label style={label}>Invoice link lifetime (days)</label>
              <input type="number" min={1} max={365} defaultValue={config.invoice_link_ttl_days ?? 7} disabled={!canConfigure}
                     onBlur={e => saveConfig({ invoice_link_ttl_days: Number(e.target.value) })} style={inputStyle} />
              <div style={hint}>How long the guest's invoice download link keeps working. Default 7 days.</div>
            </div>
          </div>
          <div style={{ ...hint, marginTop: '0.9rem' }}>
            Quiet hours only apply to events you have marked "respect quiet hours" on the Events tab.
          </div>
        </div>
      )}

      {/* ─── Templates ───────────────────────────────────────────────────── */}
      {tab === 'templates' && (
        <>
          <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, color: '#1e3a8a' }}>Message Templates</div>
              <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
                Mirror each template you approved on the CERF portal. The order of the {'{{variables}}'} you use becomes the order of the values we send.
              </div>
            </div>
            {canConfigure && (
              <button onClick={() => openEditor(null)} style={{ ...btn, background: '#2563eb', color: '#fff', whiteSpace: 'nowrap' }}>+ Add template</button>
            )}
          </div>

          {templates.length === 0 && !editor && (
            <div style={{ ...card, color: '#6b7280', fontSize: '0.85rem' }}>No templates yet.</div>
          )}

          {templates.map(t => (
            <div key={t.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 320px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#1f2937' }}>{t.template_code}</span>
                    {t.event_code && <span style={chip('#eff6ff', '#1e40af')}>{eventMeta(t.event_code)?.title || t.event_code}</span>}
                    {t.last_test_ok_at
                      ? <span style={chip('#ecfdf5', '#065f46')}>✓ tested</span>
                      : <span style={chip('#fef3c7', '#78350f')}>not tested</span>}
                    {!t.is_active && <span style={chip('#f1f5f9', '#475569')}>disabled</span>}
                  </div>
                  <div style={{ color: '#475569', fontSize: '0.82rem', marginTop: 4, whiteSpace: 'pre-wrap' }}>{t.body}</div>
                  <div style={{ ...hint, marginTop: 6 }}>
                    Template ID: <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{t.provider_template_id}</span>
                  </div>
                  <div style={hint}>
                    Params order: {(t.variables_json || []).length === 0
                      ? '— none —'
                      : (t.variables_json || []).map((v, i) => `${i + 1}. ${v}`).join('  ·  ')}
                  </div>
                </div>
                {canConfigure && (
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <button onClick={() => { setTestFor(testFor === t.id ? null : t.id); setTestResult(null); }}
                            style={{ ...btn, background: testFor === t.id ? '#fbbf24' : '#fef3c7', color: '#78350f', border: '1px solid #fde68a' }}>
                      {testFor === t.id ? 'Close' : 'Send test'}
                    </button>
                    <button onClick={() => openEditor(t)} style={{ ...btn, background: '#fff', color: '#1e40af', border: '1px solid #dbeafe' }}>Edit</button>
                    <button onClick={() => deleteTemplate(t)} style={{ ...btn, background: '#fff', color: '#991b1b', border: '1px solid #fecaca' }}>Delete</button>
                  </div>
                )}
              </div>

              {testFor === t.id && (
                <div style={{ marginTop: '0.9rem', padding: '0.8rem 1rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
                  <div style={{ fontWeight: 700, color: '#78350f', fontSize: '0.85rem', marginBottom: '0.4rem' }}>Send a real test message</div>
                  <div style={{ fontSize: '0.78rem', color: '#78350f', marginBottom: '0.6rem' }}>
                    Each value arrives as <code>TEST-1-…</code>, <code>TEST-2-…</code> in slot order. If they appear in a
                    different order than the list above, your body does not match the approved template — fix it before enabling the event.
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '1 1 200px' }}>
                      <label style={label}>Your mobile (with country code, no +)</label>
                      <input value={testMobile} onChange={e => setTestMobile(e.target.value.replace(/\D/g, '').slice(0, 15))}
                             placeholder="916374970931" style={inputStyle} />
                    </div>
                    <button onClick={() => runTest(t)} disabled={testRunning || !testMobile}
                            style={{ ...btn, background: testRunning ? '#94a3b8' : '#f59e0b', color: '#fff' }}>
                      {testRunning ? 'Sending…' : 'Send test'}
                    </button>
                  </div>
                  {testResult && (
                    <div style={{ marginTop: '0.6rem', fontSize: '0.82rem', color: testResult.success ? '#065f46' : '#991b1b', fontWeight: 600 }}>
                      {testResult.success ? '✓ Submitted — check your phone' : `✗ ${testResult.error_message || 'Failed'}`}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}

          {editor && (
            <div style={{ ...card, border: '2px solid #bfdbfe' }}>
              <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: '0.8rem' }}>
                {editor.id ? 'Edit template' : 'New template'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : '1fr 1fr', gap: '0.9rem' }}>
                <div>
                  <label style={label}>Template name (as approved on CERF)</label>
                  <input value={editor.template_code} onChange={e => setEditor(x => ({ ...x, template_code: e.target.value }))}
                         placeholder="checkout_invoice" style={inputStyle} />
                </div>
                <div>
                  <label style={label}>CERF Template ID</label>
                  <input value={editor.provider_template_id} onChange={e => setEditor(x => ({ ...x, provider_template_id: e.target.value }))}
                         placeholder="2304071377003253" style={{ ...inputStyle, fontFamily: 'monospace' }} />
                </div>
                <div>
                  <label style={label}>Display name</label>
                  <input value={editor.display_name || ''} onChange={e => setEditor(x => ({ ...x, display_name: e.target.value }))}
                         placeholder="Check-out Invoice" style={inputStyle} />
                </div>
                <div>
                  <label style={label}>Event</label>
                  <select value={editor.event_code || ''} onChange={e => setEditor(x => ({ ...x, event_code: e.target.value }))}
                          style={inputStyle}>
                    <option value="">— select an event —</option>
                    {events.map(e => <option key={e.event_code} value={e.event_code}>{e.title}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '0.9rem' }}>
                <label style={label}>Body (copy of the approved text)</label>
                <textarea value={editor.body || ''} rows={5}
                          onChange={e => setEditor(x => ({ ...x, body: e.target.value }))}
                          placeholder="Hi {{guest_name}}, thank you for staying at {{hotel_name}}. Your invoice {{invoice_no}} for Rs.{{amount}} is here: {{invoice_url}}"
                          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
                <div style={hint}>
                  This text is for your reference and for deriving the value order — WhatsApp sends Meta's approved copy, not this.
                </div>
              </div>

              {editor.event_code && (
                <div style={{ marginTop: '0.8rem' }}>
                  <div style={label}>Available variables — click to insert</div>
                  <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                    {paletteFor(editor.event_code).map(v => (
                      <button key={v} type="button"
                              onClick={() => setEditor(x => ({ ...x, body: `${x.body || ''}{{${v}}}` }))}
                              style={{ ...btn, background: '#eff6ff', color: '#1e40af', border: '1px solid #dbeafe', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {`{{${v}}}`}
                      </button>
                    ))}
                  </div>
                  <div style={{ ...hint, marginTop: 6 }}>
                    Required: {(eventMeta(editor.event_code)?.required_variables || []).join(', ') || '—'}
                  </div>
                </div>
              )}

              <div style={{ marginTop: '0.9rem', padding: '0.6rem 0.8rem', background: '#f8fafc', borderRadius: 8 }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>Values will be sent in this order</div>
                <div style={{ fontSize: '0.8rem', color: '#1f2937', fontFamily: 'monospace' }}>
                  {orderedVars(editor.body).length === 0
                    ? '— no variables used —'
                    : orderedVars(editor.body).map((v, i) => `${i + 1}. ${v}`).join('   ')}
                </div>
              </div>

              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                <button onClick={saveTemplate} disabled={savingTpl}
                        style={{ ...btn, background: savingTpl ? '#94a3b8' : '#2563eb', color: '#fff' }}>
                  {savingTpl ? 'Saving…' : 'Save template'}
                </button>
                <button onClick={() => setEditor(null)} style={{ ...btn, background: '#fff', color: '#475569', border: '1px solid #e2e8f0' }}>Cancel</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ─── Events ──────────────────────────────────────────────────────── */}
      {tab === 'events' && (
        <div style={card}>
          <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: '0.2rem' }}>Events</div>
          <div style={{ color: '#64748b', fontSize: '0.8rem', marginBottom: '0.9rem' }}>
            Bind a template to each event you want to send, then choose when it goes out.
          </div>

          {!reviewUrl && events.some(e => e.is_enabled && (e.event_code === 'CHECKOUT_REVIEW' || e.event_code === COMBINED_CHECKOUT_EVENT)) && (
            <div style={{ marginBottom: '0.9rem', padding: '0.6rem 0.9rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, color: '#78350f', fontSize: '0.82rem' }}>
              <b>No Google listing confirmed for this property.</b> A review message needs one —
              go to Front Office → Google Review → Configure, search for your hotel and confirm the listing.
              Until then the review message will be skipped.
            </div>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ background: '#f1f5f9', color: '#334155' }}>
                  <th style={{ textAlign: 'left', padding: '0.5rem' }}>Event</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem' }}>Template</th>
                  <th style={{ textAlign: 'left', padding: '0.5rem' }}>When</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem' }}>Quiet hours</th>
                  <th style={{ textAlign: 'center', padding: '0.5rem' }}>On</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => {
                  const options = templates.filter(t => t.event_code === ev.event_code && t.is_active);
                  // The combined check-out message already carries both links,
                  // so while it is on the two single events are unavailable —
                  // otherwise the guest is messaged twice.
                  const supersededByCombined = SINGLE_CHECKOUT_EVENTS.includes(ev.event_code) && combinedCheckoutOn;
                  const blockedReason = supersededByCombined
                    ? 'Covered by Invoice + Review'
                    : !ev.template_id
                      ? 'Bind a template first'
                      : !ev.tested_ok ? 'Send a successful test first' : '';
                  return (
                    <tr key={ev.event_code}
                        style={{ borderTop: '1px solid #f1f5f9', opacity: supersededByCombined ? 0.5 : 1 }}>
                      <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                        <div style={{ fontWeight: 600, color: '#1f2937' }}>{ev.title}</div>
                        <div style={{ ...hint, maxWidth: 340 }}>{ev.description}</div>
                        {ev.staff_facing && <span style={chip('#f1f5f9', '#475569')}>staff recipients</span>}
                        {ev.whatsapp_caveat === 'AUTHENTICATION_CATEGORY' && (
                          <div style={{ ...hint, color: '#92400e' }}>
                            Needs a Meta “Authentication” template — confirm CERF supports it.
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                        <select value={ev.template_id || ''} disabled={!canConfigure}
                                onChange={e => saveEvent(ev.event_code, { template_id: e.target.value || null })}
                                style={{ ...inputStyle, minWidth: 160 }}>
                          <option value="">— none —</option>
                          {options.map(t => <option key={t.id} value={t.id}>{t.template_code}</option>)}
                        </select>
                        {ev.template_id && !ev.tested_ok && (
                          <div style={{ ...hint, color: '#92400e' }}>not tested yet</div>
                        )}
                      </td>
                      <td style={{ padding: '0.5rem', verticalAlign: 'top' }}>
                        <select value={ev.send_delay_minutes ?? 0} disabled={!canConfigure}
                                onChange={e => saveEvent(ev.event_code, { send_delay_minutes: Number(e.target.value) })}
                                style={{ ...inputStyle, minWidth: 140 }}>
                          {DELAY_PRESETS.map(d => <option key={d.mins} value={d.mins}>{d.label}</option>)}
                          {!DELAY_PRESETS.some(d => d.mins === (ev.send_delay_minutes ?? 0)) && (
                            <option value={ev.send_delay_minutes}>{ev.send_delay_minutes} min</option>
                          )}
                        </select>
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', verticalAlign: 'top' }}>
                        <input type="checkbox" checked={Boolean(ev.respect_quiet_hours)} disabled={!canConfigure}
                               onChange={e => saveEvent(ev.event_code, { respect_quiet_hours: e.target.checked })} />
                      </td>
                      <td style={{ padding: '0.5rem', textAlign: 'center', verticalAlign: 'top' }}>
                        <input type="checkbox" checked={Boolean(ev.is_enabled)}
                               disabled={!canConfigure || (!ev.is_enabled && Boolean(blockedReason))}
                               title={ev.is_enabled ? '' : blockedReason}
                               onChange={e => saveEvent(ev.event_code, { is_enabled: e.target.checked })} />
                        {!ev.is_enabled && blockedReason && (
                          <div style={{ ...hint, color: '#92400e', maxWidth: 120 }}>{blockedReason}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─── Logs ────────────────────────────────────────────────────────── */}
      {tab === 'logs' && (
        <>
          {queue.length > 0 && (
            <div style={card}>
              <div style={{ fontWeight: 700, color: '#1e3a8a', marginBottom: '0.6rem' }}>Scheduled ({queue.length})</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', color: '#334155' }}>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Event</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>To</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Sends at</th>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map(q => (
                    <tr key={q.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{q.event_code}</td>
                      <td style={{ padding: '0.35rem 0.5rem', fontFamily: 'monospace' }}>{q.destination}</td>
                      <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>{new Date(q.scheduled_at).toLocaleString()}</td>
                      <td style={{ padding: '0.35rem 0.5rem' }}>{q.status}{q.attempts ? ` (${q.attempts} tries)` : ''}</td>
                      <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>
                        {canConfigure && q.status === 'PENDING' && (
                          <button onClick={() => cancelQueued(q.id)}
                                  style={{ ...btn, background: '#fff', color: '#991b1b', border: '1px solid #fecaca' }}>Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
              <div style={{ fontWeight: 700, color: '#1e3a8a' }}>Send history</div>
              <button onClick={loadLogs} style={{ ...btn, background: '#fff', color: '#1e40af', border: '1px solid #dbeafe' }}>Refresh</button>
            </div>
            {logs.length === 0 && <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nothing sent yet.</div>}
            {logs.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', color: '#334155' }}>
                      <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>When</th>
                      <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>To</th>
                      <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Event</th>
                      <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem' }}>Values sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(l => (
                      <tr key={l.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap' }}>{new Date(l.created_at).toLocaleString()}</td>
                        <td style={{ padding: '0.35rem 0.5rem', fontFamily: 'monospace' }}>{l.destination}</td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>{l.event_code || l.triggered_by || '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', color: l.success ? '#065f46' : '#991b1b', fontWeight: 600 }}>
                          {l.success ? '✓ sent' : `✗ ${l.error_message || 'failed'}`}
                        </td>
                        <td style={{ padding: '0.35rem 0.5rem', color: '#475569', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {Array.isArray(l.params_json) ? l.params_json.join(' | ') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
